import { useCallback } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { AdminPanel, type AdminScope } from './AdminPanel';

/**
 * Route des consoles d'administration — « tout dans l'URL ».
 *
 * DEUX portées, montées sur deux espaces d'URL distincts (cf. l'arbre de routes) :
 *
 *  - `gallery` (`/admin/:section`) — Admin de GALERIE. Segments :
 *      `acces` → Accès   `erreurs` → Erreurs de fichiers   `absents` → Fichiers absents
 *      `poubelle` → Poubelle   `partages` → Partages
 *    GATING : superadmin, admin, OU propriétaire d'une bibliothèque, sinon repli accueil.
 *
 *  - `system` (`/superadmin/:section`) — console SUPERADMIN. Segments :
 *      `general` → Général (nom du site)   `comptes` → Comptes   `liens` → Liens d'accès
 *      `cles-api` → Clés d'API   `journaux` → Journaux   `llm` → Configuration LLM
 *      `journal-ia` → Journal des appels IA
 *      `audit` → Journal d'audit   `reinitialiser` → Maintenance
 *    GATING : superadmin uniquement, sinon repli accueil.
 *
 * Un segment inconnu POUR LA PORTÉE → normalise vers l'entrée par défaut de la
 * portée. Le gating PAR SECTION (ex. Maintenance = superadmin dans la console
 * système) reste porté par `AdminPanel` : il ne construit/rend que les onglets
 * permis et retombe sur le premier onglet visible pour un onglet non permis.
 */

type AdminTab =
  | 'general'
  | 'users'
  | 'grants'
  | 'logs'
  | 'audit'
  | 'fileErrors'
  | 'missingFiles'
  | 'trash'
  | 'shares'
  | 'llm'
  | 'aitrace'
  | 'sessionLinks'
  | 'apiKeys'
  | 'reset';

/** Segment de route → onglet, PAR PORTÉE (segments distincts entre les deux consoles). */
const SEGMENT_TAB: Record<AdminScope, Record<string, AdminTab>> = {
  gallery: {
    acces: 'grants',
    erreurs: 'fileErrors',
    absents: 'missingFiles',
    poubelle: 'trash',
    partages: 'shares',
  },
  system: {
    general: 'general',
    comptes: 'users',
    liens: 'sessionLinks',
    'cles-api': 'apiKeys',
    journaux: 'logs',
    audit: 'audit',
    llm: 'llm',
    'journal-ia': 'aitrace',
    reinitialiser: 'reset',
  },
};

/** Onglet → segment de route, PAR PORTÉE (sous-ensemble des onglets de la portée). */
const TAB_SEGMENT: Record<AdminScope, Partial<Record<AdminTab, string>>> = {
  gallery: {
    grants: 'acces',
    fileErrors: 'erreurs',
    missingFiles: 'absents',
    trash: 'poubelle',
    shares: 'partages',
  },
  system: {
    general: 'general',
    users: 'comptes',
    sessionLinks: 'liens',
    apiKeys: 'cles-api',
    logs: 'journaux',
    audit: 'audit',
    llm: 'llm',
    aitrace: 'journal-ia',
    reset: 'reinitialiser',
  },
};

/** Base d'URL + segment par défaut de chaque portée. */
const BASE: Record<AdminScope, string> = { gallery: '/admin', system: '/superadmin' };
const DEFAULT_SEGMENT: Record<AdminScope, string> = { gallery: 'acces', system: 'comptes' };

interface AdminRouteProps {
  /** Portée de la console : galerie (`/admin`) ou système (`/superadmin`). */
  scope: AdminScope;
}

export function AdminRoute({ scope }: AdminRouteProps): React.JSX.Element {
  const { section } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSuperadmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin';
  const ownsLibrary = (user?.libraryId ?? null) !== null;
  // Galerie : superadmin, admin (écriture globale) OU propriétaire. Système :
  // superadmin uniquement (l'admin n'a AUCUN pouvoir superadmin).
  const allowed = scope === 'system' ? isSuperadmin : isSuperadmin || isAdmin || ownsLibrary;

  const base = BASE[scope];
  const onClose = useCallback(() => navigate('/'), [navigate]);
  const onTabChange = useCallback(
    (next: AdminTab) => {
      const seg = TAB_SEGMENT[scope][next];
      if (seg) navigate(`${base}/${seg}`);
    },
    [navigate, base, scope],
  );

  if (!allowed) return <Navigate to="/" replace />;

  const tab = section ? SEGMENT_TAB[scope][section] : undefined;
  // Segment inconnu pour cette portée → normalise vers l'entrée par défaut de la portée.
  if (section !== undefined && tab === undefined)
    return <Navigate to={`${base}/${DEFAULT_SEGMENT[scope]}`} replace />;

  return <AdminPanel scope={scope} onClose={onClose} tab={tab} onTabChange={onTabChange} />;
}
